import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FORM_MODE, Permissions, REST_API } from '@awp/constants';
import * as Yup from 'yup';
import { Col, FormLabel, Row } from 'react-bootstrap';
import { getAvalancheGeographicAreas } from '@awp/stores/avalancheSlice';
import { FormSelect } from '@awp/components/Common/AwpFormInputs';
import { Form, Formik } from 'formik';
import { GetJson } from '@awp/helpers/fetchReact';
import Authorize from '@awp/components/Authorize';
import { ActionMenuEdit, ActionMenuView } from '@awp/components/Common/AwpActionMenus';
import Table from '@components/table';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { isObjectEmpty, isValueEmpty } from '@awp/helpers/utilsReact';
import { Link, useSearchParams } from 'react-router-dom';
import { AwpContainer } from '@components/Common/AwpContainer';
import { useUrlParamTableFilterContext } from '@providers/UrlParamTableFilterContext';
import { getNumberFromSearchParams } from '@awp/helpers/navigation';
import { AvalanchePathsService } from '@components/AvalanchePathsAdmin/AvalanchePathsService';

const AvalanchePathsTable = ({ isLarge, isMedium }) => {
  const { defaultPageSize, index, onChangeIndex, onChangePageSize, onChangeSearch, onChangeSort, search, sort } =
    useUrlParamTableFilterContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));

  const crudService = new AvalanchePathsService(FORM_MODE.LIST);
  const { readOnly } = crudService.crud;

  const getArea = () => {
    const areaId = getNumberFromSearchParams('areaId', 0);
    return areaList.find((a) => a.value === areaId);
  };

  const [areaList, setAreaList] = useState(avalancheAreas.filter((x) => !x.isDisabled));

  const [areaPaths, setAreaPaths] = useState([]);
  const [areaPathList, setAreaPathList] = useState(areaPaths);
  const [statusFilter, setStatusFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [area, setArea] = useState(getArea() ?? {});

  const avalanchePrograms = programAreas.filter((x) => !x.isDisabled);

  const updateAreaData = (myArea) => {
    if (isObjectEmpty(myArea)) return;

    loadPathList(myArea.value);
    const program = avalanchePrograms.find((x) => x.value === myArea.parentId);
    const filterCriteria = (x) => x.parentId === program.value && !x.isDisabled;
    setAreaList(avalancheAreas.filter(filterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
  };

  useEffect(() => {
    let filtered = areaPaths;

    if (!isValueEmpty(search)) {
      filtered = filtered.filter((x) => hasSearchMatch(x.name) || hasSearchMatch(x.pathnumber));
    }
    if (statusFilter === 'Active') {
      filtered = filtered.filter((x) => !x.isDisabled);
    } else if (statusFilter === 'Inactive') {
      filtered = filtered.filter((x) => x.isDisabled);
    }

    filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
    setAreaPathList(filtered);
    if (area) {
      updateAreaData(area);
    }
  }, [search, statusFilter, area]);

  const hasSearchMatch = (src) => {
    return src ? src.toString().toUpperCase().includes(search.toUpperCase()) : false;
  };

  const loadPathList = async (id) => {
    setLoading(true);
    const urlGeographicpointslinebyarea = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicpathlinebyavalanchearea/${id}`;
    GetJson(urlGeographicpointslinebyarea)
      .then((response) => response.json())
      .then(async (records) => {
        if (records) {
          const areaPaths = records.map((x) => {
            return {
              value: x.Id,
              pathnumber: x.PathNumber,
              areaId: x.AreaId,
              programId: x.ProgramId,
              name: x.Name,
              pathcode: x.Code,
              isDisabled: x.IsActive != 'Y',
            };
          });
          let filtered = [...areaPaths];
          if (!isValueEmpty(search)) {
            filtered = filtered.filter((x) => hasSearchMatch(x.name) || hasSearchMatch(x.pathnumber));
          }
          if (statusFilter === 'Active') {
            filtered = filtered.filter((x) => !x.isDisabled);
          } else if (statusFilter === 'Inactive') {
            filtered = filtered.filter((x) => x.isDisabled);
          }
          setAreaPaths(areaPaths.sort((a, b) => b.name.localeCompare(a.name)));
          setAreaPathList(filtered.sort((a, b) => b.name.localeCompare(a.name)));
        }

        setLoading(false);
      });
  };

  const programOnChange = (opt, setFieldValue) => {
    setFieldValue('area', { value: '' }, false);
    area.value = '';
    setAreaPaths([]);
    const filterCriteria = (x) => x.parentId === opt.value && !x.isDisabled;
    setAreaList(avalancheAreas.filter(filterCriteria).sort((a, b) => a.name.localeCompare(b.name)));

    setSearchParams((params) => {
      params.delete('areaId');

      return params;
    });
  };

  const areaOnChange = (opt, setFieldValue) => {
    setArea(opt);
    // TODO useState for program
    const program = avalanchePrograms.find((x) => x.value === opt.parentId);
    setFieldValue('program', program);
    updateAreaData(opt);

    setSearchParams((params) => {
      params.set('areaId', JSON.stringify(opt.value));

      return params;
    });
  };

  const actionMenu = (row) => {
    return (
      <>
        <div>
          <Authorize requires={[Permissions.WriteAdminTools]}>
            <Link to={`${FORM_MODE.EDIT}/${row.row.original.value}`}>
              <ActionMenuEdit />
            </Link>
          </Authorize>
          <Link to={`${FORM_MODE.VIEW}/${row.row.original.value}`}>
            <ActionMenuView />
          </Link>
        </div>
      </>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'Avalanche Path Number',
        accessor: 'pathnumber',
        style: { width: '100rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Avalanche Path Name',
        accessor: 'name',
        style: { width: '100rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Avalanche Path Code',
        accessor: 'pathcode',
        style: { width: '100rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Status',
        accessor: 'isDisabled',
        style: { width: '30rem' },
        sortType: sortByNumericThenAlpha,
        Cell: (c) => {
          if (c && c.row && c.row.original) {
            const r = !c.row.original.isDisabled ? 'Active' : 'Inactive';
            return <div className="text-wrap">{r}</div>;
          }
          return '';
        },
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        style: { width: '15rem' },
        disableSortBy: true,
        Cell: actionMenu,
      },
    ],
    [isLarge, isMedium],
  );
  return (
    <>
      <AwpContainer>
        <Formik
          enableReinitialize
          validateOnMount
          validateOnBlur
          validateOnChange
          initialValues={{
            validateOnMount: true,
            program: avalanchePrograms.find((x) => x.value === area?.parentId) || null,
            area: area && Object.keys(area).length > 0 ? area : null,
          }}
          validationSchema={Yup.object({
            program: Yup.object().nullable(),
            area: Yup.object().nullable(),
          })}
        >
          {(props) => (
            <>
              <div className="d-flex">{crudService.crud.getHeader()}</div>
              <Form>
                <Row>
                  <Col sm={6}>
                    <FormSelect
                      className="avalanche-select"
                      readOnly={readOnly}
                      name="program"
                      id="program"
                      label="Program"
                      useMargin={true}
                      postChange={(e) => {
                        programOnChange(e, props.setFieldValue);
                      }}
                      options={avalanchePrograms}
                      isMulti={false}
                    />
                  </Col>
                  <Col sm={6}>
                    <FormSelect
                      className="avalanche-select"
                      readOnly={readOnly}
                      name="area"
                      id="area"
                      label="Avalanche Area"
                      useMargin={true}
                      postChange={(e) => {
                        areaOnChange(e, props.setFieldValue);
                      }}
                      options={areaList}
                      isMulti={false}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6} className="mb-4">
                    <FormLabel>
                      <b>{'Search (path name or number)'}</b>
                    </FormLabel>
                    <input
                      className="form-control"
                      placeholder="Search..."
                      type="text"
                      value={search}
                      name="search"
                      onChange={onChangeSearch}
                    ></input>
                  </Col>
                  <Col sm={2} className="mb-4">
                    <FormSelect
                      className="status-select"
                      name="statusFilter"
                      id="statusFilter"
                      label="Status"
                      isClearable={true}
                      options={[
                        { value: 'Active', label: 'Active' },
                        { value: 'Inactive', label: 'Inactive' },
                      ]}
                      postChange={(e) => setStatusFilter(e?.value ? e.value : null)}
                    />
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </Formik>
        {area?.value ? (
          <>
            <Table
              columns={columns}
              data={areaPathList}
              fetchData={() => {}}
              loading={loading}
              search=""
              defaultSortBy="Name"
              index={index}
              setIndex={onChangeIndex}
              defaultPageSize={defaultPageSize}
              setDefaultPageSize={onChangePageSize}
              sort={sort}
              setSort={onChangeSort}
            />
          </>
        ) : (
          <div className="mt-4">
            <p>Please select a program and an area to display the table.</p>
          </div>
        )}
        <div style={{ paddingTop: '1.25rem' }}>
          <Authorize requires={[Permissions.WriteAdminTools]}>
            <Link
              to={`${FORM_MODE.CREATE}${area?.value ? `?areaId=${area.value}` : ''}`}
              className="mr-2 mr-mini-0 float-left btn btn-primary"
              variant="primary"
              disabled={loading}
            >
              Create
            </Link>
          </Authorize>
        </div>
      </AwpContainer>
    </>
  );
};

export default AvalanchePathsTable;
